import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  PoundSterling, 
  FileText, 
  Plus,
  Calendar,
  Download,
  X,
  Receipt,
  ArrowLeft
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function FinancialReport() {
  const { token, API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [datePreset, setDatePreset] = useState('year_to_date');
  const [orderType, setOrderType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    vat_amount: '',
    supplier: '',
    notes: ''
  });

  // Calculate date ranges based on preset
  const getDateRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year_to_date':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'tax_year':
        // UK tax year: 6 April to 5 April
        if (today.getMonth() >= 3 && today.getDate() >= 6) {
          start = new Date(today.getFullYear(), 3, 6);
          end = new Date(today.getFullYear() + 1, 3, 5);
        } else {
          start = new Date(today.getFullYear() - 1, 3, 6);
          end = new Date(today.getFullYear(), 3, 5);
        }
        break;
      case 'last_tax_year':
        if (today.getMonth() >= 3 && today.getDate() >= 6) {
          start = new Date(today.getFullYear() - 1, 3, 6);
          end = new Date(today.getFullYear(), 3, 5);
        } else {
          start = new Date(today.getFullYear() - 2, 3, 6);
          end = new Date(today.getFullYear() - 1, 3, 5);
        }
        break;
      case 'calendar_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case 'last_calendar_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(datePreset);
      
      if (!start || !end) {
        toast.error('Please select valid dates');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/financial/report`, {
        params: { start_date: start, end_date: end, order_type: orderType },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to load report');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const { start, end } = getDateRange(datePreset);
      const response = await axios.get(`${API}/financial/expenses`, {
        params: { start_date: start, end_date: end },
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/financial/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenseCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchExpenses();
    fetchCategories();
  }, [datePreset, orderType, startDate, endDate]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/financial/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount) || 0,
        vat_amount: parseFloat(newExpense.vat_amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense added');
      setShowExpenseModal(false);
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        category: '',
        description: '',
        amount: '',
        vat_amount: '',
        supplier: '',
        notes: ''
      });
      fetchExpenses();
      fetchReport();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to add expense');
      toast.error(msg);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/financial/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense deleted');
      fetchExpenses();
      fetchReport();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value || 0);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "blue" }) => (
    <Card className={`border-l-4 border-l-${color}-500`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{formatCurrency(value)}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend).toFixed(1)}% vs last period
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="financial-report-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Report</h1>
            <p className="text-gray-500">Company financial overview and analytics</p>
          </div>
        </div>
        <Button onClick={() => setShowExpenseModal(true)} data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 block mb-1">Period</label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger data-testid="date-preset-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="this_quarter">This Quarter</SelectItem>
                  <SelectItem value="year_to_date">Year to Date</SelectItem>
                  <SelectItem value="tax_year">Current Tax Year (UK)</SelectItem>
                  <SelectItem value="last_tax_year">Last Tax Year (UK)</SelectItem>
                  <SelectItem value="calendar_year">This Calendar Year</SelectItem>
                  <SelectItem value="last_calendar_year">Last Calendar Year</SelectItem>
                  <SelectItem value="custom">Custom Dates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {datePreset === 'custom' && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">From</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">To</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </>
            )}

            <div className="w-40">
              <label className="text-sm font-medium text-gray-700 block mb-1">Order Type</label>
              <Select value={orderType} onValueChange={setOrderType}>
                <SelectTrigger data-testid="order-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="memorials">Memorials Only</SelectItem>
                  <SelectItem value="worktops">Worktops Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" onClick={fetchReport}>
              <Calendar className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          {report?.period && (
            <p className="text-sm text-gray-500 mt-3">
              Showing data from <strong>{report.period.start}</strong> to <strong>{report.period.end}</strong>
            </p>
          )}
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Revenue"
              value={report.summary.total_revenue}
              subtitle={`${report.summary.order_count} accepted orders`}
              icon={PoundSterling}
              color="blue"
            />
            <StatCard
              title="Payments Received"
              value={report.summary.total_payments_received}
              subtitle={`${formatCurrency(report.summary.total_outstanding)} outstanding`}
              icon={TrendingUp}
              color="green"
            />
            <StatCard
              title="VAT Collected"
              value={report.summary.total_vat_collected}
              subtitle="Output VAT"
              icon={FileText}
              color="purple"
            />
            <StatCard
              title="Total Expenses"
              value={report.summary.total_expenses}
              subtitle={`${formatCurrency(report.summary.total_expense_vat)} VAT reclaimable`}
              icon={Receipt}
              color="red"
            />
          </div>

          {/* Net Revenue Card */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-gradient-to-r from-blue-50 to-green-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg text-gray-600">Net Revenue (After Expenses)</p>
                    <p className="text-4xl font-bold text-gray-900">{formatCurrency(report.summary.net_revenue)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Average Order Value</p>
                    <p className="text-2xl font-semibold text-gray-700">{formatCurrency(report.summary.average_order_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {(report.summary.total_quoted > 0 || report.summary.total_quoted_count > 0) && (
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg text-amber-700">Pending Quotes</p>
                      <p className="text-4xl font-bold text-amber-900">{formatCurrency(report.summary.total_quoted)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-amber-600">Not yet accepted</p>
                      <p className="text-2xl font-semibold text-amber-700">{report.summary.total_quoted_count} quote{report.summary.total_quoted_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Monthly Revenue Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Monthly Revenue & Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.monthly_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                      <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Order Type Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Memorials', value: report.memorials.revenue },
                          { name: 'Worktops', value: report.worktops.revenue }
                        ].filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        <Cell fill="#3b82f6" />
                        <Cell fill="#10b981" />
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center"><span className="w-3 h-3 bg-blue-500 rounded mr-2"></span>Memorials</span>
                    <span className="font-medium">{formatCurrency(report.memorials.revenue)} ({report.memorials.order_count} orders)</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center"><span className="w-3 h-3 bg-green-500 rounded mr-2"></span>Worktops</span>
                    <span className="font-medium">{formatCurrency(report.worktops.revenue)} ({report.worktops.order_count} orders)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* VAT Quarterly & Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* VAT Quarterly */}
            <Card>
              <CardHeader>
                <CardTitle>VAT Quarterly Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {report.vat_quarterly && report.vat_quarterly.length > 0 ? (
                  <div className="space-y-3">
                    {report.vat_quarterly.map((q, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium text-gray-900 mb-2">{q.quarter}</div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Collected</span>
                            <p className="font-medium text-green-600">{formatCurrency(q.vat_collected)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">On Expenses</span>
                            <p className="font-medium text-red-600">{formatCurrency(q.vat_on_expenses)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Net VAT</span>
                            <p className="font-medium text-blue-600">{formatCurrency(q.net_vat)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No quarterly data available</p>
                )}
              </CardContent>
            </Card>

            {/* Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Orders by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {report.status_breakdown && Object.keys(report.status_breakdown).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(report.status_breakdown)
                      .sort((a, b) => b[1].value - a[1].value)
                      .map(([status, data], idx) => (
                        <div key={status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2" 
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span className="capitalize">{status.replace(/-/g, ' ').replace(/_/g, ' ')}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-medium">{formatCurrency(data.value)}</span>
                            <span className="text-gray-500 text-sm ml-2">({data.count})</span>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No status data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Expenses & Top Materials */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Expenses by Category */}
            <Card>
              <CardHeader>
                <CardTitle>Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {report.expenses_by_category && Object.keys(report.expenses_by_category).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(report.expenses_by_category)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([category, data], idx) => (
                        <div key={category} className="flex items-center justify-between p-2 bg-red-50 rounded">
                          <span>{category}</span>
                          <div className="text-right">
                            <span className="font-medium text-red-600">{formatCurrency(data.total)}</span>
                            <span className="text-gray-500 text-sm ml-2">({data.count} items)</span>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-3">No expenses recorded</p>
                    <Button variant="outline" size="sm" onClick={() => setShowExpenseModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Expense
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Materials */}
            <Card>
              <CardHeader>
                <CardTitle>Top Materials (Worktops)</CardTitle>
              </CardHeader>
              <CardContent>
                {report.top_materials && report.top_materials.length > 0 ? (
                  <div className="space-y-2">
                    {report.top_materials.slice(0, 8).map((material, idx) => (
                      <div key={material.material} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                        <span>{material.material}</span>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(material.value)}</span>
                          <span className="text-gray-500 text-sm ml-2">({material.count} orders)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No worktop data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Expenses Table */}
          {expenses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Description</th>
                        <th className="text-left p-2">Supplier</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-right p-2">VAT</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.slice(0, 20).map((expense) => (
                        <tr key={expense.id} className="border-b hover:bg-gray-50">
                          <td className="p-2">{expense.date}</td>
                          <td className="p-2">{expense.category}</td>
                          <td className="p-2">{expense.description}</td>
                          <td className="p-2">{expense.supplier || '-'}</td>
                          <td className="p-2 text-right font-medium">{formatCurrency(expense.amount)}</td>
                          <td className="p-2 text-right text-gray-500">{formatCurrency(expense.vat_amount)}</td>
                          <td className="p-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Add Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md m-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Expense</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowExpenseModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddExpense} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Date</label>
                  <Input
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <Select 
                    value={newExpense.category} 
                    onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    placeholder="What was this expense for?"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Amount (£)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">VAT (£)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newExpense.vat_amount}
                      onChange={(e) => setNewExpense({ ...newExpense, vat_amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Supplier (optional)</label>
                  <Input
                    value={newExpense.supplier}
                    onChange={(e) => setNewExpense({ ...newExpense, supplier: e.target.value })}
                    placeholder="Supplier name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <Input
                    value={newExpense.notes}
                    onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                    placeholder="Additional notes"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowExpenseModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    Add Expense
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
